/**
 * One-tap starter prompts for an empty chat conversation (Figma BI node
 * 2002-36): an optional heading over a vertical stack of pill buttons. Tapping a
 * pill fires `onPick` with its prompt — the parent decides how to send it. Pure
 * and presentational; the caller supplies already-translated strings.
 *
 * Entrance: each row slides in from the top + fades, staggered by
 * {@link STAGGER_MS}. It's a CSS enter animation (tailwindcss-animate), so it
 * replays whenever this component mounts — i.e. on first visit and after the
 * chat is cleared (BiChat keys `ChatWindow` by its reset counter, remounting it
 * — and us — on Clear). `animation-fill-mode: backwards` holds each row in its
 * pre-enter state during its delay so nothing flashes in early; reduced-motion
 * users get the final state with no movement.
 */
interface EmptyConversationSuggestionsProps {
  /** Prompt to send when a pill is tapped — one entry per pill. */
  readonly prompts: string[];
  /** Optional heading above the pills (e.g. "Que voulez-vous analyser ?"). */
  readonly heading?: string;
  readonly onPick: (prompt: string) => void;
}

/** Delay between each row's entrance (ms). */
const STAGGER_MS = 200;
/** Duration of each row's slide-in (ms). */
const ENTER_MS = 300;

/** Animation classes shared by the heading + pills (see component docstring). */
const ENTER_CLASSES =
  "animate-in fade-in slide-in-from-top-2 motion-reduce:animate-none";

/** Per-row enter animation: staggered delay, fixed duration, hold-before state. */
function enterStyle(stepIndex: number): React.CSSProperties {
  return {
    animationDuration: `${ENTER_MS}ms`,
    animationDelay: `${stepIndex * STAGGER_MS}ms`,
    animationFillMode: "backwards",
  };
}

export function EmptyConversationSuggestions({
  prompts,
  heading,
  onPick,
}: EmptyConversationSuggestionsProps) {
  if (prompts.length === 0) return null;

  // The heading takes the first stagger step; pills follow.
  const headingOffset = heading ? 1 : 0;

  return (
    <div className="flex flex-col gap-2" data-testid="bi-chat-suggestions">
      {heading && (
        <p
          className={`px-1 text-[15px] font-medium text-gray-950 ${ENTER_CLASSES}`}
          style={enterStyle(0)}
        >
          {heading}
        </p>
      )}
      {prompts.map((prompt, index) => (
        <button
          key={`${index}-${prompt}`}
          type="button"
          onClick={() => onPick(prompt)}
          data-testid="bi-suggestion-pill"
          style={enterStyle(headingOffset + index)}
          className={`rounded-[20px] bg-gray-100 px-4 py-2.5 text-left text-[15px] font-medium text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700 ${ENTER_CLASSES}`}
        >
          {prompt}
        </button>
      ))}
    </div>
  );
}
